import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Search } from 'lucide-react';
import { guestsQuery, type Guest } from '@/lib/guests';
import { usePageHeader } from '@/components/PageHeader';
import { ApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';

const LIMIT = 50;

export const Route = createFileRoute('/_app/guests/')({
  component: GuestsPage,
});

function GuestsPage() {
  const [inputValue, setInputValue] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, error } = useQuery(guestsQuery(q, LIMIT));

  const is401 = error instanceof ApiError && error.status === 401;
  const errorMessage = is401
    ? 'Your session has expired.'
    : error instanceof ApiError
      ? error.message
      : error
        ? String(error)
        : null;

  const items = data?.items ?? [];
  const atLimit = items.length === LIMIT;

  // Title lives in the Topbar (navTitle); publish the live result count as the
  // subtitle (the response is items-only — no total — and capped at LIMIT, so
  // show "50+" when at the cap). undefined until data loads.
  usePageHeader({
    subtitle: data
      ? `${items.length}${atLimit ? '+' : ''} guest${items.length === 1 ? '' : 's'}`
      : undefined,
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(inputValue.trim());
  }

  return (
    <>
      {/* Search form */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2">
        <Input
          type="search"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Name, email, or phone…"
          leadingIcon={<Search />}
          aria-label="Search guests by name, email, or phone"
          className="flex-1"
        />
        <Button type="submit" variant="primary">
          Search
        </Button>
      </form>

      {/* Loading */}
      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {/* Error */}
      {errorMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>
            {errorMessage}
            {is401 && (
              <>
                {' '}
                <Link to="/login" className="font-semibold underline">
                  Sign in again
                </Link>
                .
              </>
            )}
          </span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !errorMessage && data && items.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-5 py-12 text-center">
          <Search className="mx-auto mb-3 h-8 w-8 text-muted" aria-hidden="true" />
          <p className="text-sm text-muted">
            {q
              ? 'No guests found. Try a different search term.'
              : 'Search guests by name, email, or phone.'}
          </p>
        </div>
      )}

      {/* Results */}
      {items.length > 0 && (
        <section>
          {atLimit && (
            <p className="mb-3 text-sm text-muted">
              Showing first {LIMIT} results — refine your search to narrow down.
            </p>
          )}
          <GuestsTable items={items} />
        </section>
      )}
    </>
  );
}

export function GuestsTable({ items }: { items: Guest[] }) {
  return (
    <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th scope="col" className="px-4 py-3">Full name</th>
            <th scope="col" className="px-4 py-3">Email</th>
            <th scope="col" className="px-4 py-3">Phone</th>
            <th scope="col" className="px-4 py-3">Language</th>
          </tr>
        </thead>
        <tbody>
          {items.map((guest) => (
            <tr
              key={guest.id}
              className="border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <Avatar name={guest.full_name} size="sm" />
                  <Link
                    to="/guests/$id"
                    params={{ id: guest.id }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {guest.full_name}
                  </Link>
                </div>
              </td>
              <td className="px-4 py-3 text-muted">{guest.email ?? '—'}</td>
              <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted">
                {guest.phone ?? '—'}
              </td>
              <td className="px-4 py-3 text-muted">{guest.language ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
